import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, 
    User, 
    Mail,
    Calendar,
    CreditCard,
    Check,
    Sparkles,
    ArrowUpRight,
    FileText,
    XCircle,
    Heart,
    Shield,
    Clock,
    ChevronRight,
    Loader2,
    Gift,
    Star
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateMark } from '../components/Logo';
import { PromoCodeEntry } from '../components/PromoCodeEntry';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Account = () => {
    const navigate = useNavigate();
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [tiers, setTiers] = useState([]);
    const [billingHistory, setBillingHistory] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [showBillingHistory, setShowBillingHistory] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [promoStatus, setPromoStatus] = useState(null);

    useEffect(() => {
        fetchTiers();
        fetchPromoStatus();
    }, []);

    const fetchPromoStatus = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            if (!token) return;
            const response = await fetch(`${API_URL}/api/promo/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPromoStatus(data);
            }
        } catch (error) {
            console.error('Error fetching promo status:', error);
        }
    };

    const fetchTiers = async () => {
        try {
            const response = await fetch(`${API_URL}/api/subscription-tiers`);
            if (response.ok) {
                const data = await response.json();
                setTiers(data.tiers || []);
            }
        } catch (error) {
            console.error('Error fetching tiers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBillingHistory = async () => {
        setBillingLoading(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/billing-history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setBillingHistory(data.invoices || []);
            }
        } catch (error) {
            toast.error('Failed to load billing history');
        } finally {
            setBillingLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/cancel`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Subscription will be canceled at end of billing period');
                setShowCancelConfirm(false);
                if (refreshUser) refreshUser();
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to cancel subscription');
            }
        } catch (error) {
            toast.error('Failed to cancel subscription');
        } finally {
            setCancelLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/reactivate`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Subscription reactivated!');
                if (refreshUser) refreshUser();
            } else {
                toast.error('Failed to reactivate subscription');
            }
        } catch (error) {
            toast.error('Failed to reactivate subscription');
        }
    };

    // Get current tier info
    const currentTier = tiers.find(t => t.id === user?.subscription_tier) || tiers.find(t => t.id === 'free');
    const currentTierIndex = tiers.findIndex(t => t.id === user?.subscription_tier);
    const availableUpgrades = tiers.filter((t, i) => i > currentTierIndex && t.id !== 'free');

    // Format tier name
    const getTierDisplayName = (tierId) => {
        const tier = tiers.find(t => t.id === tierId);
        return tier?.name || tierId?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Free';
    };

    // Get tier color
    const getTierColor = (tierId) => {
        const colors = {
            'free': '#7D8E95',
            'essentials': '#1D6B6B',
            'support_plus': '#1D6B6B',
            'partner': '#E08850'
        };
        return colors[tierId] || '#7D8E95';
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-transparent flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B] mx-auto mb-4" />
                    <BodyText className="text-[#7D8E95]">Loading your account...</BodyText>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate(-1)}
                        data-testid="back-btn"
                        className="text-[#8B7B6B] hover:text-[#6B5D4D] hover:bg-[#FFBB98]/40"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg">My Account</Heading>
                    <button
                        onClick={() => navigate('/home')}
                        className="hover:opacity-80 transition-opacity"
                        data-testid="header-logo"
                        aria-label="Go to dashboard"
                    >
                        <CoRegulateMark size={28} />
                    </button>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* Profile Section */}
                <section className="mb-8 brand-fade-in">
                    <div className="glass-card rounded-2xl p-5">
                        {/* Background accent */}
                        <div 
                            className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
                            style={{ background: getTierColor(user?.subscription_tier), transform: 'translate(30%, -30%)' }}
                        />
                        
                        <div className="flex items-start gap-5 relative">
                            <div 
                                className="w-20 h-20 rounded-full flex items-center justify-center shrink-0"
                                style={{
                                    background: `linear-gradient(135deg, ${getTierColor(user?.subscription_tier)}40 0%, ${getTierColor(user?.subscription_tier)}20 100%)`,
                                    border: `2px solid ${getTierColor(user?.subscription_tier)}50`
                                }}
                            >
                                <User className="w-9 h-9" style={{ color: getTierColor(user?.subscription_tier) }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <Heading as="h2" size="text-xl" className="mb-1 truncate">
                                    {user?.name || 'Welcome'}
                                </Heading>
                                <div className="flex items-center gap-2 text-[#7D8E95] mb-3">
                                    <Mail className="w-4 h-4 shrink-0" />
                                    <SmallText className="truncate">{user?.email}</SmallText>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-[#7D8E95]" />
                                    <SmallText className="text-[#7D8E95]">
                                        Member since {formatDate(user?.created_at)}
                                    </SmallText>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Current Plan Section */}
                <section className="mb-8 brand-fade-in" style={{ animationDelay: '50ms' }}>
                    <div className="flex items-center justify-between mb-4">
                        <SmallText className="uppercase tracking-wider text-[#7D8E95] font-medium">
                            Current Plan
                        </SmallText>
                        {user?.subscription_tier && user.subscription_tier !== 'free' && (
                            <SmallText className="text-[#7D8E95]">
                                {user.subscription_billing === 'yearly' ? 'Yearly billing' : 'Monthly billing'}
                            </SmallText>
                        )}
                    </div>
                    
                    <BrandCard 
                        variant="elevated" 
                        padding="lg"
                        className="relative overflow-hidden"
                        style={{
                            borderColor: getTierColor(user?.subscription_tier),
                            borderWidth: '2px'
                        }}
                    >
                        {/* Plan badge */}
                        {user?.subscription_tier && user.subscription_tier !== 'free' && (
                            <div 
                                className="absolute top-0 right-0 px-3 py-1 text-white text-xs font-medium rounded-bl-xl"
                                style={{ background: getTierColor(user?.subscription_tier) }}
                            >
                                Active
                            </div>
                        )}
                        
                        <div className="flex items-start gap-4">
                            <div 
                                className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                                style={{ background: `${getTierColor(user?.subscription_tier)}20` }}
                            >
                                {user?.subscription_tier === 'partner' ? (
                                    <Heart className="w-7 h-7" style={{ color: getTierColor(user?.subscription_tier) }} />
                                ) : user?.subscription_tier === 'support_plus' ? (
                                    <Sparkles className="w-7 h-7" style={{ color: getTierColor(user?.subscription_tier) }} />
                                ) : user?.subscription_tier === 'essentials' ? (
                                    <Shield className="w-7 h-7" style={{ color: getTierColor(user?.subscription_tier) }} />
                                ) : (
                                    <User className="w-7 h-7" style={{ color: getTierColor(user?.subscription_tier) }} />
                                )}
                            </div>
                            <div className="flex-1">
                                <Heading as="h3" size="text-xl" style={{ color: getTierColor(user?.subscription_tier) }}>
                                    {getTierDisplayName(user?.subscription_tier)}
                                </Heading>
                                <BodyText className="text-[#7D8E95] mt-1">
                                    {currentTier?.description || 'Explore CoRegulate at your own pace'}
                                </BodyText>
                                
                                {/* Price */}
                                {currentTier && currentTier.monthlyPrice > 0 && (
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span className="text-2xl font-semibold" style={{ color: getTierColor(user?.subscription_tier) }}>
                                            ${user?.subscription_billing === 'yearly' ? currentTier.yearlyPrice : currentTier.monthlyPrice}
                                        </span>
                                        <SmallText className="text-[#7D8E95]">
                                            /{user?.subscription_billing === 'yearly' ? 'year' : 'month'}
                                        </SmallText>
                                    </div>
                                )}
                                
                                {/* Cancellation notice */}
                                {user?.subscription_cancel_at_period_end && (
                                    <div className="mt-3 p-3 bg-[#FFBB98]/20 rounded-xl flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-[#E08850]" />
                                        <SmallText className="text-[#E08850]">
                                            Cancels at end of billing period
                                        </SmallText>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Features included */}
                        {currentTier?.enabledFeatures && currentTier.enabledFeatures.length > 0 && (
                            <div className="mt-5 pt-5 border-t border-white/40/30">
                                <SmallText className="text-[#7D8E95] font-medium mb-3 block">What's included:</SmallText>
                                <div className="grid grid-cols-2 gap-2">
                                    {currentTier.enabledFeatures.slice(0, 6).map((feature, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <Check className="w-4 h-4 text-[#1D6B6B]" />
                                            <SmallText className="text-[#344648]">
                                                {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </SmallText>
                                        </div>
                                    ))}
                                </div>
                                {currentTier.enabledFeatures.length > 6 && (
                                    <SmallText className="text-[#7D8E95] mt-2">
                                        +{currentTier.enabledFeatures.length - 6} more features
                                    </SmallText>
                                )}
                            </div>
                        )}
                    </BrandCard>
                </section>

                {/* Upgrade Options */}
                {availableUpgrades.length > 0 && (
                    <section className="mb-8 brand-fade-in" style={{ animationDelay: '100ms' }}>
                        <SmallText className="uppercase tracking-wider text-[#7D8E95] font-medium mb-4 block">
                            Upgrade Options
                        </SmallText>
                        
                        <div className="space-y-3">
                            {availableUpgrades.map((tier) => (
                                <button
                                    key={tier.id}
                                    onClick={() => navigate('/pricing')}
                                    className="w-full p-4 rounded-2xl glass-card card-hover text-left group flex items-center gap-4"
                                    data-testid={`upgrade-${tier.id}`}
                                >
                                    <div 
                                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                                        style={{ background: `${tier.color}20` }}
                                    >
                                        {tier.id === 'partner' ? (
                                            <Heart className="w-6 h-6" style={{ color: tier.color }} />
                                        ) : (
                                            <Sparkles className="w-6 h-6" style={{ color: tier.color }} />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <BodyText className="font-semibold" style={{ color: tier.color }}>
                                                {tier.name}
                                            </BodyText>
                                            {tier.highlighted && (
                                                <span className="px-2 py-0.5 text-xs bg-[#1D6B6B] text-white rounded-full">
                                                    Popular
                                                </span>
                                            )}
                                        </div>
                                        <SmallText className="text-[#7D8E95]">
                                            ${tier.monthlyPrice}/mo or ${tier.yearlyPrice}/yr
                                        </SmallText>
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-[#7D8E95] group-hover:text-[#1D6B6B] transition-colors" />
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Promo Code Section - Show for free users OR users with active promo */}
                {(user?.subscription_tier === 'free' || !user?.subscription_tier || promoStatus?.has_promo) && (
                    <section className="mb-8 brand-fade-in" style={{ animationDelay: '120ms' }}>
                        {promoStatus?.has_promo ? (
                            // Show promo status for users with active promo
                            <BrandCard variant="rose" className="p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFBB98] to-[#E09060] flex items-center justify-center">
                                        <Gift className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <Heading level={4} className="text-[#1D6B6B]">Beta Tester Access</Heading>
                                        <SmallText className="text-[#7D8E95]">
                                            Code: {promoStatus.promo_code}
                                        </SmallText>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 rounded-xl bg-white/50">
                                        <SmallText className="text-[#7D8E95]">Access Level</SmallText>
                                        <BodyText className="text-[#1D6B6B] font-medium">
                                            {promoStatus.tier_granted?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </BodyText>
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-xl bg-white/50">
                                        <SmallText className="text-[#7D8E95]">Free Until</SmallText>
                                        <BodyText className="text-[#1D6B6B] font-medium">
                                            {promoStatus.expires_at ? new Date(promoStatus.expires_at).toLocaleDateString('en-AU', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) : 'N/A'}
                                        </BodyText>
                                    </div>

                                    {/* Feedback Progress */}
                                    <div className="p-3 rounded-xl bg-white/50">
                                        <div className="flex items-center justify-between mb-2">
                                            <SmallText className="text-[#7D8E95]">Feedback Progress</SmallText>
                                            <SmallText className="text-[#1D6B6B] font-medium">
                                                {promoStatus.feedback_count || 0} / {promoStatus.feedback_required_count || 5}
                                            </SmallText>
                                        </div>
                                        <div className="h-2 bg-[#7D8E95]/20 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-gradient-to-r from-[#1D6B6B] to-[#FFBB98] rounded-full transition-all duration-500"
                                                style={{ 
                                                    width: `${Math.min(100, ((promoStatus.feedback_count || 0) / (promoStatus.feedback_required_count || 5)) * 100)}%` 
                                                }}
                                            />
                                        </div>
                                        {promoStatus.feedback_met ? (
                                            <div className="flex items-center gap-2 mt-2">
                                                <Check className="w-4 h-4 text-green-600" />
                                                <SmallText className="text-green-600">Feedback requirement met!</SmallText>
                                            </div>
                                        ) : (
                                            <SmallText className="text-[#7D8E95] mt-2">
                                                Rate {(promoStatus.feedback_required_count || 5) - (promoStatus.feedback_count || 0)} more tools to maintain free access
                                            </SmallText>
                                        )}
                                    </div>
                                </div>
                            </BrandCard>
                        ) : (
                            // Show promo code entry for users without promo
                            <PromoCodeEntry onSuccess={() => {
                                fetchPromoStatus();
                                if (refreshUser) refreshUser();
                            }} />
                        )}
                    </section>
                )}

                {/* Subscription Management (for paid users) */}
                {user?.subscription_tier && user.subscription_tier !== 'free' && (
                    <section className="mb-8 brand-fade-in" style={{ animationDelay: '150ms' }}>
                        <SmallText className="uppercase tracking-wider text-[#7D8E95] font-medium mb-4 block">
                            Manage Subscription
                        </SmallText>
                        
                        <div className="space-y-3">
                            {/* Billing History */}
                            <button
                                onClick={() => {
                                    setShowBillingHistory(true);
                                    fetchBillingHistory();
                                }}
                                className="w-full p-4 rounded-2xl glass-card card-hover text-left flex items-center gap-4"
                                data-testid="billing-history-btn"
                            >
                                <IconContainer variant="blue" size="md" useGradient={true}>
                                    <FileText className="w-5 h-5" />
                                </IconContainer>
                                <div className="flex-1">
                                    <BodyText className="font-medium text-[#1D6B6B]">Billing History</BodyText>
                                    <SmallText className="text-[#7D8E95]">View past invoices and receipts</SmallText>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                            </button>

                            {/* Payment Method */}
                            <button
                                onClick={() => toast.info('Payment method management coming soon')}
                                className="w-full p-4 rounded-2xl glass-card card-hover text-left flex items-center gap-4"
                                data-testid="payment-method-btn"
                            >
                                <IconContainer variant="sage" size="md" useGradient={true}>
                                    <CreditCard className="w-5 h-5" />
                                </IconContainer>
                                <div className="flex-1">
                                    <BodyText className="font-medium text-[#1D6B6B]">Payment Method</BodyText>
                                    <SmallText className="text-[#7D8E95]">Update your card details</SmallText>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                            </button>
                            
                            {/* Cancel / Reactivate */}
                            {user.subscription_cancel_at_period_end ? (
                                <button
                                    onClick={handleReactivateSubscription}
                                    className="w-full p-4 rounded-2xl glass-card card-hover text-left flex items-center gap-4"
                                    data-testid="reactivate-btn"
                                >
                                    <IconContainer variant="sage" size="md" useGradient={true}>
                                        <Heart className="w-5 h-5" />
                                    </IconContainer>
                                    <div className="flex-1">
                                        <BodyText className="font-medium text-[#1D6B6B]">Reactivate Subscription</BodyText>
                                        <SmallText className="text-[#7D8E95]">Keep your access to premium features</SmallText>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowCancelConfirm(true)}
                                    className="w-full p-4 rounded-2xl glass-card card-hover text-left flex items-center gap-4"
                                    data-testid="cancel-subscription-btn"
                                >
                                    <IconContainer variant="rose" size="md" useGradient={true}>
                                        <XCircle className="w-5 h-5" />
                                    </IconContainer>
                                    <div className="flex-1">
                                        <BodyText className="font-medium text-[#E08850]">Cancel Subscription</BodyText>
                                        <SmallText className="text-[#7D8E95]">You'll keep access until period ends</SmallText>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95]" />
                                </button>
                            )}
                        </div>
                    </section>
                )}

                {/* Quick Actions */}
                <section className="mb-8 brand-fade-in" style={{ animationDelay: '200ms' }}>
                    <div className="flex gap-3">
                        <SecondaryButton 
                            onClick={() => navigate('/settings')} 
                            className="flex-1"
                            data-testid="settings-btn"
                        >
                            App Settings
                        </SecondaryButton>
                        <SecondaryButton 
                            onClick={() => navigate('/support')} 
                            className="flex-1"
                            data-testid="support-btn"
                        >
                            Get Support
                        </SecondaryButton>
                    </div>
                </section>
            </main>

            {/* Billing History Modal */}
            {showBillingHistory && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="text-lg">Billing History</Heading>
                            <IconButton onClick={() => setShowBillingHistory(false)}>
                                <ArrowLeft className="w-5 h-5" />
                            </IconButton>
                        </div>
                        
                        {billingLoading ? (
                            <div className="text-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-[#1D6B6B] mx-auto mb-4" />
                                <SmallText className="text-[#7D8E95]">Loading invoices...</SmallText>
                            </div>
                        ) : billingHistory.length === 0 ? (
                            <div className="text-center py-8">
                                <FileText className="w-12 h-12 text-[#FFBB98] mx-auto mb-3" />
                                <SmallText className="text-[#7D8E95]">No billing history yet</SmallText>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {billingHistory.map(invoice => (
                                    <div key={invoice.id} className="p-4 rounded-xl bg-[#F9F7F4] border border-white/40">
                                        <div className="flex items-center justify-between mb-2">
                                            <BodyText className="font-medium">
                                                ${invoice.amount?.toFixed(2)} {invoice.currency?.toUpperCase()}
                                            </BodyText>
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                invoice.status === 'paid' 
                                                    ? 'bg-[#7D8E95]/20 text-[#1D6B6B]' 
                                                    : 'bg-[#FFBB98]/20 text-[#E08850]'
                                            }`}>
                                                {invoice.status}
                                            </span>
                                        </div>
                                        <SmallText className="text-[#7D8E95] block">
                                            {new Date(invoice.date * 1000).toLocaleDateString()}
                                        </SmallText>
                                        {invoice.invoice_pdf && (
                                            <a 
                                                href={invoice.invoice_pdf} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-sm text-[#1D6B6B] hover:underline mt-2 inline-flex items-center gap-1"
                                            >
                                                <FileText className="w-3 h-3" />
                                                Download PDF
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cancel Confirmation Modal */}
            {showCancelConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-8 h-8 text-[#E08850]" />
                            </div>
                            <Heading as="h3" size="text-lg" className="mb-2">Cancel Subscription?</Heading>
                            <BodyText className="text-[#7D8E95]">
                                You'll keep access until the end of your billing period. You can reactivate anytime.
                            </BodyText>
                        </div>
                        
                        <div className="flex gap-3">
                            <TertiaryButton 
                                onClick={() => setShowCancelConfirm(false)} 
                                className="flex-1"
                            >
                                Keep Plan
                            </TertiaryButton>
                            <SecondaryButton 
                                onClick={handleCancelSubscription}
                                disabled={cancelLoading}
                                className="flex-1 !border-[#E08850] !text-[#E08850]"
                            >
                                {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Account;
